import { computed, MaybeRefOrGetter, reactive, toValue, watch } from "vue"
import { useDataGroupMemberStore } from "@/gp/DataGroupMemberStore"
import { useDataGroupStore } from "@/gp/DataGroupStore"
import { useFamilyGroupMemberManager } from "./FamilyGroupMemberManager"

export type ProfileSharingState = ReturnType<typeof useProfileSharingState>

export function useProfileSharingState(familyGroupId: MaybeRefOrGetter<string>) {
    const memberStore = useDataGroupMemberStore()
    const dataGroupStore = useDataGroupStore()
    const memberManager = useFamilyGroupMemberManager()

    const sharedGroupIdsRef = computed(() => {
        const dms = memberStore.getMembersForFamilyGroups([toValue(familyGroupId)])
        const dgs = dataGroupStore.getLoadedGroupList(dms.map(dm => dm.dataGroupId))
        return new Set(dgs.filter(dg => dg.isProfile).map(dg => dg.id!))
    })

    const addedProfileGroupIds = reactive(new Set<string>())
    const removedProfileGroupIds = reactive(new Set<string>())

    function isShared(profileGroupId: string | undefined) {
        // NOTE: race condition could result in same id being in the added and removed sets, in which case "current" state 
        // is really undefined (depends on which API call finishes last), so doesn't matter what we return in that case
        return !!profileGroupId && 
            (sharedGroupIdsRef.value.has(profileGroupId) || addedProfileGroupIds.has(profileGroupId)) && 
            !removedProfileGroupIds.has(profileGroupId)
    }

    function shareProfiles(profileGroupIds: string[], value: boolean) {
        if (value) {
            const idsToAdd = profileGroupIds.filter(id => !isShared(id))
            idsToAdd.forEach(id => addedProfileGroupIds.add(id))
            console.log(`ProfileSharingState: adding ${JSON.stringify(idsToAdd)}`)
            memberManager.shareProfilesAsync(toValue(familyGroupId), idsToAdd) // fire-and-forget
        }
        else {
            const idsToRemove = profileGroupIds.filter(id => isShared(id))
            idsToRemove.forEach(id => removedProfileGroupIds.add(id))
            console.log(`ProfileSharingState: removing ${JSON.stringify(idsToRemove)}`)
            memberManager.removeSharedProfilesAsync(toValue(familyGroupId), idsToRemove) // fire-and-forget
        }
    }

    watch(() => toValue(familyGroupId), () => {
        // reset added/removed sets when family group changes
        addedProfileGroupIds.clear()
        removedProfileGroupIds.clear()
        console.log(`ProfileSharingState: reset state for family group ${toValue(familyGroupId)}`)
    })

    // watch for changes in sharedGroupIds and update added/removed sets when changes are completed
    watch(sharedGroupIdsRef, () => {
        for (const id of sharedGroupIdsRef.value) {
            if (addedProfileGroupIds.delete(id)) { // officially added now
                console.log(`ProfileSharingState: Profile group ${id} is now added`)
            }
        }
        for (const id of [...removedProfileGroupIds]) {
            if (!sharedGroupIdsRef.value.has(id)) {
                removedProfileGroupIds.delete(id) // officially removed now
                console.log(`ProfileSharingState: Profile group ${id} is now removed`)
            }
        }
    })

    return {
        isShared,
        shareProfiles,
    }
}
